/**
 * Date of Birth — three boxed inputs (DD / MM / YYYY) with auto-advance focus,
 * matching the design. Digits only; validation (range + real date) runs on blur
 * of the whole group and again on submit — never mutating what was typed.
 */
import { FocusEvent, forwardRef, useRef } from 'react'
import { FieldError, FieldLabel } from './FieldLabel'

export type DobField = 'day' | 'month' | 'year'

interface Props {
  day: string
  month: string
  year: string
  onChange: (field: DobField, value: string) => void
  /** Fired when focus leaves the whole DD/MM/YYYY group (not when tabbing between them). */
  onBlur?: () => void
  error?: string
}

const digitsOnly = (s: string, max: number) => s.replace(/\D/g, '').slice(0, max)

interface BoxProps {
  value: string
  placeholder: string
  onChange: (value: string) => void
  wide?: boolean
  ariaLabel: string
}

const Box = forwardRef<HTMLInputElement, BoxProps>(function Box(
  { value, placeholder, onChange, wide, ariaLabel },
  ref
) {
  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={`${wide ? 'min-w-0 flex-1' : 'w-20 shrink-0'} rounded-xl border border-textSeparator bg-transparent px-4 py-3 text-center text-labelLarge text-textPrimary placeholder:text-textDisabled focus:border-textAccentPrimary focus:outline-none`}
    />
  )
})

export function DobBoxes({ day, month, year, onChange, onBlur, error }: Props) {
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  // Only validate once focus leaves the whole group — relatedTarget is the element
  // gaining focus; if it's still inside the row, we're just tabbing between boxes.
  const handleGroupBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur?.()
  }

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel required>Date of Birth</FieldLabel>
      <div className="flex items-center gap-3" onBlur={handleGroupBlur}>
        <Box
          value={day}
          placeholder="DD"
          ariaLabel="Day"
          onChange={(v) => {
            const next = digitsOnly(v, 2)
            onChange('day', next)
            if (next.length === 2) monthRef.current?.focus()
          }}
        />
        <Box
          ref={monthRef}
          value={month}
          placeholder="MM"
          ariaLabel="Month"
          onChange={(v) => {
            const next = digitsOnly(v, 2)
            onChange('month', next)
            if (next.length === 2) yearRef.current?.focus()
          }}
        />
        <Box
          ref={yearRef}
          value={year}
          placeholder="YYYY"
          ariaLabel="Year"
          wide
          onChange={(v) => onChange('year', digitsOnly(v, 4))}
        />
      </div>
      <FieldError message={error} />
    </div>
  )
}
